import { Button, Spinner } from '@genie-fintech/ui/components'
import { container, innerContainer, line, modalFooter } from './styles.css'
import * as Dialog from '@radix-ui/react-dialog'
import { useRequest, useToggle } from 'ahooks'
import { useCallback } from 'react'
import { Password } from '@genie-fintech/ui/components/hook-fields'
import { useController, useForm } from 'react-hook-form'
import { DEFAULT_FORM_VALUES, schema, TField } from './constants'
import { zodResolver } from '@hookform/resolvers/zod'
import { putServiceUserPhone } from '$services/api'
import { useShallow } from 'zustand/react/shallow'
import { toast } from 'sonner'
import { getPhone } from '$app/utils'
import { Icon } from '@genie-fintech/ui/icons'
import { useProfileDataStore } from '$layouts/UserProfileLayout/hooks/useProfileDataStore'
import { useIsMounted } from '@genie-fintech/ui/hooks'
import { trueOrUndefined } from '@genie-fintech/ui/functions'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { flexColumn } from '$styles/common.css'
import Phone from '$components/Phone'
import { ChevronLeft } from 'lucide-react'
import { usePermissions } from '$hooks/actions'
import { PERMISSIONS } from '$app/constants/common'
import { status } from '@genie-fintech/ui/style/element'

const { colors } = themeVars

const ChangePhoneSetting = () => {
  const isMounted = useIsMounted()

  const [modalOpen, { toggle }] = useToggle()

  const { checkPermissions } = usePermissions()
  const CAN_VIEW_PHONE = checkPermissions([PERMISSIONS.USER_PHONE_NUMBER_VIEW])
  const CAN_EDIT_PHONE = checkPermissions([PERMISSIONS.USER_PHONE_NUMBER_EDIT])

  const { control, handleSubmit, reset } = useForm<TField>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES
  })

  const { field: phoneCodeField } = useController({
    name: 'phone_code',
    control
  })
  const { field: phoneNoField } = useController({
    name: 'phone_no',
    control
  })

  const profile = useProfileDataStore(useShallow(state => state.profile))

  const updateProfilePartially = useProfileDataStore(
    state => state.updateProfilePartially
  )

  const {
    phone_code,
    phone_no,
    id: userId = '',
    is_phone_verified
  } = profile ?? {}

  const hasPhone = phone_code || phone_no

  const {
    runAsync: updateServiceUserPhone,
    loading: updatingServiceUserPhone
  } = useRequest(putServiceUserPhone, {
    manual: true,
    onSuccess: (_, [{ phone_code, phone_no }]) => {
      toast.success(`Phone has been ${hasPhone ? 'updated' : 'added'}!`)
      updateProfilePartially({ phone_code, phone_no })
      toggle()
      reset(DEFAULT_FORM_VALUES)
    }
  })

  const onCancel = useCallback(() => {
    reset(DEFAULT_FORM_VALUES)
    toggle()
  }, [reset, toggle])

  const onContinue = useCallback(
    (payload: TField) => {
      updateServiceUserPhone({
        userId,
        ...payload
      })
    },
    [updateServiceUserPhone, userId]
  )

  if (!CAN_VIEW_PHONE) return null

  return (
    <>
      <article className={container} data-ready={trueOrUndefined(isMounted)}>
        <article className={innerContainer}>
          <article className={flexColumn} style={{ gap: 4, flex: 1 }}>
            <p className={title.two}>ADD/EDIT PHONE NUMBER</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              You can update your phone number with anytime.
            </p>
          </article>

          <span
            className={status({
              size: 'tiny',
              type: 'faint',
              kind: is_phone_verified ? 'success' : 'warning'
            })}
            style={{ textTransform: 'uppercase' }}
          >
            {is_phone_verified ? 'Phone Verified' : 'Phone Unverified'}
          </span>

          <p className={title.five} style={{ color: colors.text.disabled }}>
            {getPhone({
              phoneCode: phone_code,
              phoneNo: phone_no
            })}
          </p>

          {CAN_EDIT_PHONE && (
            <Button
              onClick={toggle}
              styleVariants={{
                kind: 'neutral',
                type: 'outlined',
                size: 'small'
              }}
            >
              {!hasPhone && <Icon namespace="Add" />}
              {hasPhone ? 'Change' : 'Add Phone Number'}
            </Button>
          )}
        </article>
      </article>

      <Dialog.Root open={modalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="DialogOverlay" />
          <Dialog.Content
            className="DialogContent flex flex-col max-w-[500px]"
            aria-describedby=""
          >
            <Dialog.Title className="hidden" />

            <form onSubmit={handleSubmit(onContinue)}>
              <main className={flexColumn} style={{ padding: 32, gap: 24 }}>
                <article className={flexColumn} style={{ gap: 4, flex: 1 }}>
                  <p className={title.two}>
                    {hasPhone ? 'ENTER NEW PHONE NUMBER' : 'ADD PHONE NUMBER'}
                  </p>
                  <p
                    className={footnote.one}
                    style={{ color: colors.neutral[60] }}
                  >
                    {hasPhone
                      ? 'Enter new phone number to continue the changes.'
                      : 'Enter phone number to continue the process.'}
                  </p>
                </article>

                <article className={flexColumn} style={{ gap: 32 }}>
                  <Password
                    name="password"
                    control={control}
                    label="Password"
                    inputProps={{ autoComplete: 'password' }}
                    description="Enter your login password to confirm the changes."
                    required
                  />

                  <hr className={line} />

                  <article className={flexColumn} style={{ gap: 16 }}>
                    {hasPhone && (
                      <Phone
                        label={'Current Phone Number'}
                        inputProps={{
                          defaultValue: phone_no ?? '',
                          readOnly: true
                        }}
                        callingCodeProps={{
                          defaultValue: phone_code ?? '',
                          disabled: true
                        }}
                        disabled
                        required
                      />
                    )}

                    <Phone
                      label={hasPhone ? 'New Phone Number' : 'Phone Number'}
                      inputProps={phoneNoField}
                      callingCodeProps={phoneCodeField}
                      required
                    />
                  </article>
                </article>
              </main>

              <footer className={modalFooter}>
                <Button
                  type="button"
                  styleVariants={{
                    kind: 'neutral',
                    type: 'outlined',
                    size: 'small'
                  }}
                  onClick={onCancel}
                  disabled={updatingServiceUserPhone}
                >
                  <ChevronLeft size={16} />
                  Back
                </Button>

                <Button
                  type="submit"
                  styleVariants={{ size: 'small' }}
                  disabled={updatingServiceUserPhone}
                >
                  {updatingServiceUserPhone && <Spinner />}
                  {hasPhone ? 'Continue' : 'Add'}
                </Button>
              </footer>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

export default ChangePhoneSetting
